import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useUser } from '@/allauth_auth/hooks.js';
import {
  USER_PROFILE_QUERY_KEY,
  useUserProfileData,
} from '@/hooks/useUserProfileData';
import { apiPut } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { ProfileData } from '@/lib/profile/types';
import { getBrowserTimezone } from '@/lib/scheduling/timezone';

/**
 * When the authenticated user has never explicitly chosen a timezone,
 * populate one once from the browser's resolved IANA zone via
 * PUT /users/api/profile/.
 *
 * Set-if-unset, once per session: never overwrites a user-chosen value, and
 * makes at most one attempt regardless of outcome (success, validation
 * rejection, or transient failure), so a permanently-invalid browser zone or a
 * profile-endpoint outage can never produce a request loop or block render.
 */
export function useEnsureUserTimezone(enabled: boolean): void {
  const { data: profileData, dataUpdatedAt } = useUserProfileData({ enabled });
  const queryClient = useQueryClient();
  // The authoritative "who is signed in right now" id, from the auth context.
  // The profile query is cached under a user-agnostic key, so we cross-check
  // the fetched profile against this before acting (see the guard below).
  const authUser = useUser() as { id?: number } | null;
  const authUserId = authUser?.id;
  // Tracks the user id we've already attempted a backfill for, keyed by user so
  // the one-shot guard resets when a different user signs in without
  // TeamProvider remounting (logout -> login on a shared device). A plain
  // boolean would stay `true` across that transition and leave the second
  // user's blank timezone permanently unpopulated.
  const attemptedForUserIdRef = useRef<number | null>(null);
  // Tracks the authenticated user id we've already forced a profile refetch
  // for. See the identity-mismatch branch below: a stale cross-user cache entry
  // is repaired exactly once per identity, never in a loop.
  const refreshedForUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Disabled transition (e.g. logout). Clear the per-session guards so a
      // later authenticated session can retry. Keying the guards by user id
      // already lets a *different* user backfill after a switch, but the *same*
      // user logging out and back in without a TeamProvider remount would keep
      // their id in attemptedForUserIdRef and stay permanently suppressed.
      // Within a single enabled session neither ref is touched here, preserving
      // the one-attempt-per-session guarantee.
      attemptedForUserIdRef.current = null;
      refreshedForUserIdRef.current = null;
      return;
    }
    // Only decide once we hold a profile that came from a successful server
    // fetch — never the stale snapshot React Query hydrates as initialData
    // (persisted with initialDataUpdatedAt === 0). A stale blank snapshot could
    // otherwise fire a PUT that overwrites a timezone the user set elsewhere
    // (e.g. Settings on another device) before the fresh fetch resolves.
    if (dataUpdatedAt === 0) {
      return;
    }
    const user = profileData?.user;
    if (!user) {
      return;
    }
    if (typeof authUserId !== 'number') {
      return;
    }
    // The profile query is cached under a user-agnostic key
    // (USER_PROFILE_QUERY_KEY), so after an authenticated-user switch without a
    // remount (logout -> login on a shared device) it can hold the previous
    // user's profile. Because that cached result is a fresh fetch (guarded
    // above by dataUpdatedAt !== 0) it stays within staleTime, so React Query
    // will not refetch on its own — leaving the new user's blank timezone
    // suppressed indefinitely. Force a one-shot refetch so the current user's
    // profile loads and the backfill can run, then bail this pass. The ref
    // guard keys the refetch to authUserId so we never loop if a fetch keeps
    // resolving to the wrong identity (e.g. a transient auth/session desync).
    if (user.id !== authUserId) {
      if (refreshedForUserIdRef.current !== authUserId) {
        refreshedForUserIdRef.current = authUserId;
        queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      }
      return;
    }
    // One attempt per user. A different user id means a new session on this
    // mount, so the guard is effectively reset for them.
    if (attemptedForUserIdRef.current === user.id) {
      return;
    }
    // Gate on timezone_is_set, not on timezone itself: the read path always
    // resolves timezone to a valid zone ("UTC" when unset), so an empty-string
    // check can never fire. timezone_is_set === false is the only signal that
    // the user hasn't explicitly chosen one. Treat undefined as "already
    // set — do nothing" (only occurs in fixtures that omit the field).
    if (user.timezone_is_set !== false) {
      return;
    }
    const browserTimezone = getBrowserTimezone();
    if (!browserTimezone) {
      return;
    }
    // Mark BEFORE issuing the request so every outcome counts as this user's
    // single attempt — no retry loop against an invalid value or a failing
    // endpoint.
    attemptedForUserIdRef.current = user.id;

    apiPut('/users/api/profile/', { timezone: browserTimezone })
      .then(() => {
        // Write the known-good value straight into the client cache rather than
        // invalidating. The profile PUT busts the server-side profile_data
        // cache, but an immediate refetch is still an unnecessary round-trip on
        // this path — setQueryData applies the value we just persisted so
        // Settings and downstream reads see it immediately. Patch both fields:
        // timezone_is_set must flip to true alongside timezone, or Settings
        // (useProfile.ts) would show the field blank and timezone-picker
        // surfaces (WelcomeProposalContent.tsx) would keep preselecting the
        // browser suggestion over the value we just saved.
        //
        // Guard the patch by the id of the user we issued the PUT for. The
        // profile query is cached under a user-agnostic key, so if the user
        // switched accounts while this PUT was in flight the cache now holds a
        // DIFFERENT user's profile (repaired by the identity-mismatch refetch
        // above). Patching it then would stamp this user's browser timezone and
        // timezone_is_set=true onto the wrong account, suppressing that user's
        // own backfill. Only patch when the cached profile is still this user's.
        queryClient.setQueryData<ProfileData>(USER_PROFILE_QUERY_KEY, prev =>
          prev && prev.user.id === user.id
            ? {
                ...prev,
                user: {
                  ...prev.user,
                  timezone: browserTimezone,
                  timezone_is_set: true,
                },
              }
            : prev
        );
      })
      .catch((err: unknown) => {
        logger.error(
          'Failed to populate user timezone from browser',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'timezone', browserTimezone }
        );
      });
  }, [enabled, profileData, dataUpdatedAt, authUserId, queryClient]);
}
